import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  onSnapshot,
} from "firebase/firestore";
import { db, auth } from "../authentication/firebase";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router";
import LoadingButton from "../components/LoadingButton";
import BackdropWrapper from "../components/modals/BackdropWrapper";
import ActionModal from "../components/modals/ActionModal";
import Info from "../components/modals/Info";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Header from "../styles/Header";

const Cart = () => {
  const [cartProducts, setCartProducts] = useState([]);
  const [cartItems, setCartItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [checkoutError, setCheckoutError] = useState({
    active: false,
    error: "",
  });

  const navigate = useNavigate();

  const getCartProducts = async () => {
    const productsQuery = query(
      collection(db, "products"),
      where("active", "==", true),
      where("metadata.item_type", "==", "cart")
    );

    let fetchedProducts = [];
    try {
      const productsSnapshot = await getDocs(productsQuery);
      const promises = productsSnapshot.docs.map(async (product) => {
        const pricesRef = collection(db, "products", product.id, "prices");
        const pricesSnapshot = await getDocs(pricesRef);

        const productData = product.data();
        const prices = pricesSnapshot.docs.map((price) => ({
          id: price.id,
          ...price.data(),
        }));

        fetchedProducts.push({
          id: product.id,
          ...productData,
          prices: prices,
          // Use the first price as default, or you could implement price selection logic
          defaultPrice: prices[0] || null,
        });
      });

      await Promise.all(promises);
      console.log("cart products", fetchedProducts);
      setCartProducts(fetchedProducts);
    } catch (e) {
      console.log("error while fetching cart products", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCartProducts();
  }, []);

  const updateQuantity = (productId, change) => {
    setCartItems((prev) => {
      const currentQty = prev[productId] || 0;
      const newQty = Math.max(0, currentQty + change);

      if (newQty === 0) {
        const { [productId]: removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [productId]: newQty,
      };
    });
  };

  const calculateSubtotal = () => {
    return Object.entries(cartItems).reduce((total, [productId, quantity]) => {
      const product = cartProducts.find((p) => p.id === productId);
      if (product && product.defaultPrice) {
        return total + (product.defaultPrice.unit_amount / 100) * quantity;
      }
      return total;
    }, 0);
  };

  const getCartItemsForCheckout = () => {
    return Object.entries(cartItems).map(([productId, quantity]) => {
      const product = cartProducts.find((p) => p.id === productId);
      return {
        price: product.defaultPrice.id,
        quantity: quantity,
      };
    });
  };

  const checkoutHandler = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setActionModal(true);
      return;
    }

    const lineItems = getCartItemsForCheckout();
    if (lineItems.length === 0) {
      return;
    }

    setCheckoutLoading(true);
    try {
      const collectionRef = collection(
        db,
        "customers",
        uid,
        "checkout_sessions"
      );

      const docRef = await addDoc(collectionRef, {
        line_items: lineItems,
        mode: "payment",
        success_url: `${window.location.origin}/payment-successful`,
        cancel_url: `${window.location.origin}/cart?error=true`,
      });

      onSnapshot(docRef, (snap) => {
        const { error, url } = snap?.data();
        if (error) {
          setCheckoutError({ active: true, error: error.message });
          console.log("error", error);
          setCheckoutLoading(false);
        } else if (url) {
          window.location.assign(url);
        }
      });
    } catch (e) {
      setCheckoutLoading(false);
      console.log("error while checkout redirecting", e);
    }
  };

  const backdropHandler = () => {
    setActionModal((prev) => !prev);
  };

  const actionModalFunction = () => {
    backdropHandler();
    navigate("/login", { state: { from: "/cart" } });
  };

  const checkoutBackdropHandler = () => {
    setCheckoutError((prev) => ({
      ...prev,
      active: !prev.active,
    }));
  };

  const subtotal = calculateSubtotal();
  const itemCount = Object.values(cartItems).reduce(
    (total, qty) => total + qty,
    0
  );

  return (
    <>
      <Header
        title="Products"
        description="More than products, they’re symbols of change. Crafted with care, rooted in purpose, each piece carries Wroot’s mission forward. Wear it. Carry it. Share it."
      />

      {loading ? (
        <CartContainer>
          <LoadingContainer>
            <CircularProgress color="inherit" />
          </LoadingContainer>
        </CartContainer>
      ) : (
        <>
          <BackdropWrapper
            open={actionModal}
            smallSize={true}
            backdropHandler={backdropHandler}
            element={
              <ActionModal
                heading={"Login Required"}
                msg={"Please login or signup before checkout"}
                backdropHandler={backdropHandler}
                buttonName={"Login"}
                action={actionModalFunction}
              />
            }
          />
          <BackdropWrapper
            open={checkoutError.active}
            smallSize={true}
            backdropHandler={checkoutBackdropHandler}
            element={
              <Info
                heading={"Checkout Error"}
                msg={"Please contact us to solve this issue"}
                backdropHandler={checkoutBackdropHandler}
              />
            }
          />

          <CartContainer>
            <CartHeader>
              <p>
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </p>
            </CartHeader>

            <CartContent>
              <CartItemsSection>
                <CartTable>
                  <CartTableHeader>
                    <th>Product Details</th>
                    <th>Quantity</th>
                    <th>Price</th>
                  </CartTableHeader>
                  <tbody>
                    {cartProducts.map((product) => {
                      const quantity = cartItems[product.id] || 0;
                      const price = product.defaultPrice
                        ? product.defaultPrice.unit_amount / 100
                        : 0;
                      
                      // Get inventory settings from metadata
                      const maxQuantity = parseInt(product.metadata?.max_quantity) || 99;
                      const isOutOfStock = product.metadata?.out_of_stock === 'true';
                      const isMaxReached = quantity >= maxQuantity;
                      const canIncrease = !isOutOfStock && !isMaxReached;

                      return (
                        <CartRow key={product.id}>
                          <ProductDetailsCell>
                            <ProductImage
                              src={
                                product.images?.[0] || "/api/placeholder/80/80"
                              }
                              alt={product.name}
                            />
                            <ProductInfo>
                              <ProductNameRow>
                                <ProductName>{product.name}</ProductName>
                                {isOutOfStock && (
                                  <OutOfStockBadge>Out of Stock</OutOfStockBadge>
                                )}
                              </ProductNameRow>
                              <ProductDescription>
                                {product.description}
                              </ProductDescription>
                            </ProductInfo>
                          </ProductDetailsCell>

                          <QuantityCell>
                            <QuantityControls>
                              <QuantityButton
                                onClick={() => updateQuantity(product.id, -1)}
                                disabled={quantity === 0}
                              >
                                <RemoveIcon />
                              </QuantityButton>
                              <QuantityInputContainer>
                                <QuantityInput value={quantity} readOnly />
                                {(isMaxReached && !isOutOfStock) && (
                                  <InventoryStatus>
                                    Max {maxQuantity}
                                  </InventoryStatus>
                                )}
                              </QuantityInputContainer>
                              <QuantityButton
                                onClick={() => updateQuantity(product.id, 1)}
                                disabled={!canIncrease}
                                title={
                                  isOutOfStock 
                                    ? "Out of Stock" 
                                    : isMaxReached 
                                    ? `Maximum ${maxQuantity} per customer` 
                                    : ""
                                }
                              >
                                <AddIcon />
                              </QuantityButton>
                            </QuantityControls>
                          </QuantityCell>

                          <PriceCell>${price.toFixed(2)}</PriceCell>
                        </CartRow>
                      );
                    })}
                  </tbody>
                </CartTable>
              </CartItemsSection>

              <OrderSummary>
                <SummaryTitle>Total</SummaryTitle>

                <SummaryRow>
                  <span className="label">Sub-Total</span>
                  <span className="value">${subtotal.toFixed(2)}</span>
                </SummaryRow>

                <DeliverySection>
                  <span className="label">
                    Delivery
                  </span>
                  <DeliveryDropdown>
                    <option>USPS Standard Shipping (2-5 business days)</option>
                  </DeliveryDropdown>
                </DeliverySection>

                <CheckoutButton
                  loading={checkoutLoading}
                  onClick={checkoutHandler}
                  disabled={itemCount === 0}
                >
                  Check Out
                </CheckoutButton>

                <PaymentMethods>
                  <DiscountText>
                    Got A discount code? add in the next step
                  </DiscountText>
                </PaymentMethods>
              </OrderSummary>
            </CartContent>
          </CartContainer>
        </>
      )}
    </>
  );
};

const CartContainer = styled.div`
  width: var(--section-width);
  margin: var(--section-margin) auto;
  padding: 2rem 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const CartHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2.5rem;
    color: #333;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.1rem;
  }
`;

const CartContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const CartItemsSection = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const CartTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const CartTableHeader = styled.thead`
  background: #f8f9fa;

  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #e9ecef;

    &:first-child {
      width: 60%;
    }
    &:nth-child(2) {
      width: 25%;
    }
    &:nth-child(3) {
      width: 15%;
    }

    @media (max-width: 768px) {
      padding: 0.75rem 0.5rem;
      font-size: 0.9rem;

      &:first-child {
        width: 50%;
      }
      &:nth-child(2) {
        width: 30%;
      }
      &:nth-child(3) {
        width: 20%;
      }
    }

    @media (max-width: 480px) {
      padding: 0.5rem 0.25rem;
      font-size: 0.8rem;

      &:first-child {
        width: 45%;
      }
      &:nth-child(2) {
        width: 35%;
      }
      &:nth-child(3) {
        width: 20%;
      }
    }
  }
`;

const CartRow = styled.tr`
  border-bottom: 1px solid #e9ecef;

  &:hover {
    background: #f8f9fa;
  }
`;

const ProductDetailsCell = styled.td`
  padding: 1.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.25rem;
    gap: 0.5rem;
  }
`;

const ProductImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  background: #f0f0f0;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }

  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 0.25rem;
  }
`;

const ProductName = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const OutOfStockBadge = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
  animation: pulse-red 2s infinite;
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
    padding: 3px 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.6rem;
    padding: 2px 5px;
    border-radius: 8px;
  }
`;

const ProductDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    -webkit-line-clamp: 1;
  }
`;

const QuantityCell = styled.td`
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.25rem;
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f0f0f0;
    border-color: #bbb;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: #f8f8f8;
    border-color: #e8e8e8;
    
    svg {
      color: #ccc;
    }
  }

  svg {
    font-size: 16px;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;

    svg {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    width: 24px;
    height: 24px;

    svg {
      font-size: 12px;
    }
  }
`;

const QuantityInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const QuantityInput = styled.input`
  width: 60px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  font-size: 1rem;

  @media (max-width: 768px) {
    width: 50px;
    padding: 6px;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    width: 40px;
    padding: 4px;
    font-size: 0.8rem;
  }
`;

const InventoryStatus = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  text-align: center;
  margin: 0;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: block;
  width: fit-content;
  background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
  color: #663c00;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 2px 5px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.55rem;
    padding: 2px 4px;
    border-radius: 6px;
  }
`;

const PriceCell = styled.td`
  padding: 1rem;
  font-weight: 600;
  color: #333;

  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.25rem;
    font-size: 0.8rem;
  }
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const SummaryTitle = styled.h2`
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
  color: #333;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  .label {
    color: #666;
    font-weight: 500;
  }

  .value {
    font-weight: 600;
    color: #333;
  }
`;

const DeliverySection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;

  .label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #666;
    font-weight: 500;
    flex-shrink: 0;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;


const DeliveryDropdown = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  min-width: 250px;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    min-width: 200px;
    font-size: 0.85rem;
  }
  
  @media (max-width: 480px) {
    min-width: 100%;
    margin-top: 8px;
  }
`;

const CheckoutButton = styled(LoadingButton)`
  width: 100%;
  padding: 1rem 2rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 1.5rem;

  &:hover:not(:disabled) {
    background: #218838;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PaymentMethods = styled.div`
  text-align: center;
`;

const DiscountText = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
`;

export default Cart;
